var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { useClasses, useConsoleLog, useLayoutProps, useProps, useTranslate } from '@hawkins/helpers';
import IconChevronLeft from '@hawkins/icons/lib/iconChevronLeft';
import IconChevronRight from '@hawkins/icons/lib/iconChevronRight';
import { Button } from '../button';
import { useId } from '../hooks';
import { Text } from '../text';
import { TimePicker } from '../timePicker';
import { CalendarBody } from './calendarBody';
import { ChangeCurrentMonthOrYear } from './changeCurrentMonthOrYear';
import { DatePickerStrings } from './datePicker.strings';
import { useDatePickerStyles } from './datePicker.styles';
import { DATEPICKER_INFINITY } from './datePicker.types';
import { DatePickerQuickSelect } from './datePickerQuickSelect';
import { dayjs } from './dayjs';
var displayName = 'DatePicker';
/**
 * Util function that guards against invalid props being set on the DatePicker
 * and throws a JS error if an invalid set of props were found
 */
var checkPropsAreValid = function (props) {
    if (typeof props.selected === 'string') {
        if (typeof props.onSelectedChange !== 'function')
            return false;
    }
    return true;
};
/**
 * Default aria-role that will apply to the root DatePicker component
 */
export var DATEPICKER_DEFAULT_ROLE = 'region';
/**
 * The `<DatePicker>` component is used to allow the user to select a date or a range of dates.
 *
 * This component is the calendar itself. If you need a calendar to be rendered on the page,
 * you will use this component.
 *
 * `<DatePickerDropdown />` and `<DatePickerInput />` can also be used to render a dropdown or
 * an input to capture the user selection. These components utilize this `<DatePicker />` to
 * handle all the calendar controls and displaying.
 *
 * NOTE: This component will *not* render any children provided to it, as this is an incompatible concept
 * for the DatePicker
 */
export var DatePicker = React.forwardRef(function (_a, ref) {
    var _b;
    var _c;
    var _ = _a.children, receivedProps = __rest(_a, ["children"]);
    // props
    var props = useProps(displayName, receivedProps);
    // We could increase the specificity of the TypeScript typings,
    // but this breaks our API table generation once we start going deep
    // into discriminated unions for ensuring props exist
    useConsoleLog(!checkPropsAreValid(props), "Invalid DatePicker props detected. A \"selected\" date value was provided to the DatePicker without also providing an \"onSelectedChange\" handler", { level: 'error', printInProd: true });
    var className = props.className, classesProp = props.classes, _d = props.component, RootComponent = _d === void 0 ? 'div' : _d, disabled = props.disabled, footer = props.footer, highlightedRangesProp = props.highlightedRanges, highlightToday = props.highlightToday, _e = props.monthsToDisplay, monthsToDisplay = _e === void 0 ? 1 : _e, startingDate = props.startingDate, id = props.id, layout = props.layout, minSelectableDate = props.minSelectableDate, maxSelectableDate = props.maxSelectableDate, onCurrentDateChange = props.onCurrentDateChange, onGoToNextMonth = props.onGoToNextMonth, onGoToPreviousMonth = props.onGoToPreviousMonth, onSelectedChange = props.onSelectedChange, onSelectingRange = props.onSelectingRange, _f = props.pickerVariant, pickerVariant = _f === void 0 ? 'datepicker' : _f, range = props.range, quickSelect = props.quickSelect, _g = props.role, role = _g === void 0 ? DATEPICKER_DEFAULT_ROLE : _g, selectedProp = props.selected, styleProp = props.style, _h = props.tabIndex, tabIndex = _h === void 0 ? 0 : _h, timePickerProps = props.timePickerProps, translateProp = props.translate, rest = __rest(props, ["className", "classes", "component", "disabled", "footer", "highlightedRanges", "highlightToday", "monthsToDisplay", "startingDate", "id", "layout", "minSelectableDate", "maxSelectableDate", "onCurrentDateChange", "onGoToNextMonth", "onGoToPreviousMonth", "onSelectedChange", "onSelectingRange", "pickerVariant", "range", "quickSelect", "role", "selected", "style", "tabIndex", "timePickerProps", "translate"]);
    // prop coercion
    var quickSelectEnabled = (typeof quickSelect === 'boolean' && quickSelect) || Object.values(quickSelect !== null && quickSelect !== void 0 ? quickSelect : {}).some(function (v) { return v; });
    var maxSelectableDateLocalTime = maxSelectableDate
        ? dayjs(maxSelectableDate).startOf('day').toISOString()
        : undefined;
    var minSelectableDateLocalTime = minSelectableDate
        ? dayjs(minSelectableDate).startOf('day').toISOString()
        : undefined;
    // strings
    var translate = useTranslate(translateProp, DatePickerStrings);
    // internal state
    var _j = __read(React.useState(function () {
        return startingDate ? dayjs(startingDate).startOf('month').toISOString() : dayjs().startOf('month').toISOString();
    }), 2), dateInternal = _j[0], setDateInternal = _j[1];
    var _k = __read(React.useState(), 2), selectedDayInternal = _k[0], setSelectedDayInternal = _k[1];
    var _l = __read(React.useState(-1), 2), showChangeCurrentMonth = _l[0], setShowChangeCurrentMonth = _l[1];
    var _m = __read(React.useState(-1), 2), showChangeCurrentYear = _m[0], setShowChangeCurrentYear = _m[1];
    var _o = __read(React.useState(function () { return monthsToDisplay - 1; }), 2), calIndexHasNextBtn = _o[0], setCalIndexHasNextBtn = _o[1];
    var _p = __read(React.useState(''), 2), rangeAnchor = _p[0], setRangeAnchor = _p[1];
    var _q = __read(React.useState(''), 2), rangeHover = _q[0], setRangeHover = _q[1];
    // refs
    var datepickerIdInternal = useId('datepicker-');
    var datepickerId = id !== null && id !== void 0 ? id : datepickerIdInternal;
    // pre-memos
    var highlightedRanges = React.useMemo(function () {
        return highlightedRangesProp === null || highlightedRangesProp === void 0 ? void 0 : highlightedRangesProp.map(function (_a) {
            var _b = __read(_a, 2), start = _b[0], end = _b[1];
            return [
                start !== DATEPICKER_INFINITY ? dayjs(start).startOf('day').toISOString() : start,
                end !== DATEPICKER_INFINITY ? dayjs(end).startOf('day').toISOString() : end,
            ];
        });
    }, [highlightedRangesProp]);
    var _r = React.useMemo(function () {
        var s = selectedProp !== null && selectedProp !== void 0 ? selectedProp : selectedDayInternal;
        if (!s)
            return { selected: s, selectedWithTime: s };
        if (Array.isArray(s)) {
            return {
                selected: s.map(function (day) { return dayjs(day).startOf('day').toISOString(); }),
                selectedWithTime: s.map(function (day) { return dayjs(day).toISOString(); }),
            };
        }
        return { selected: dayjs(s).startOf('day').toISOString(), selectedWithTime: dayjs(s).toISOString() };
    }, [selectedDayInternal, selectedProp]), selected = _r.selected, selectedWithTime = _r.selectedWithTime;
    // memos for callbacks
    var rangeTuple = React.useMemo(function () {
        if (!rangeAnchor)
            return [];
        var anchord = dayjs(rangeAnchor);
        if (!rangeHover)
            return [anchord.toISOString()];
        var hovered = dayjs(rangeHover);
        var out = [hovered.toISOString(), anchord.toISOString()];
        if (hovered.isAfter(anchord))
            return out.reverse();
        return out;
    }, [rangeAnchor, rangeHover]);
    // callbacks for memos 🙃
    var checkDateSelected = React.useCallback(function (date) {
        var dateToCheck = dayjs(date).startOf('day');
        var dateToCheckISO = dateToCheck.toISOString();
        var _a = __read(rangeTuple.length > 0 ? rangeTuple : Array.isArray(selected) ? selected : [], 2), start = _a[0], end = _a[1];
        if (start && end) {
            if ((dateToCheck.isSame(start) || dateToCheck.isAfter(start)) &&
                (dateToCheck.isBefore(end) || dateToCheck.isSame(end))) {
                return true;
            }
            return false;
        }
        if (!selected)
            return false;
        return selected === dateToCheckISO;
    }, [rangeTuple, selected]);
    // styles
    var baseClasses = useDatePickerStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, className, (_b = {},
        _b[classes.rootHasQuickSelect] = quickSelectEnabled,
        _b));
    var style = useLayoutProps({ layout: layout, style: styleProp });
    // callbacks
    var handleGoToPreviousMonth = React.useCallback(function () {
        onGoToPreviousMonth === null || onGoToPreviousMonth === void 0 ? void 0 : onGoToPreviousMonth();
        setDateInternal(dayjs(dateInternal).subtract(monthsToDisplay, 'month').toISOString());
    }, [dateInternal, monthsToDisplay, onGoToPreviousMonth]);
    var handleGoToNextMonth = React.useCallback(function () {
        onGoToNextMonth === null || onGoToNextMonth === void 0 ? void 0 : onGoToNextMonth();
        setDateInternal(dayjs(dateInternal).add(monthsToDisplay, 'month').toISOString());
    }, [dateInternal, monthsToDisplay, onGoToNextMonth]);
    var handleSelectDayOrDays = React.useCallback(function (dateISO) {
        var dateToUse = dateISO;
        if (pickerVariant === 'datetimepicker' && selectedWithTime) {
            var currentSelectedWithTime = dayjs(Array.isArray(selectedWithTime) ? selectedWithTime[0] : selectedWithTime);
            dateToUse = dayjs(Array.isArray(dateISO) ? dateISO[0] : dateISO)
                .set('hours', currentSelectedWithTime.get('hours'))
                .set('minutes', currentSelectedWithTime.get('minutes'))
                .set('seconds', currentSelectedWithTime.get('seconds'))
                .set('milliseconds', currentSelectedWithTime.get('milliseconds'))
                .toISOString();
        }
        if (onSelectedChange)
            return onSelectedChange(dateToUse);
        return setSelectedDayInternal(dateToUse);
    }, [onSelectedChange, selectedWithTime, pickerVariant]);
    var handleTimePickerChange = React.useCallback(function (time) {
        if (!time)
            return;
        var dateWithTime = dayjs((Array.isArray(selected) ? selected[0] : selected) || dayjs().toISOString());
        dateWithTime = dateWithTime
            .set('hours', time.getHours())
            .set('minutes', time.getMinutes())
            .set('seconds', time.getSeconds())
            .set('milliseconds', time.getMilliseconds());
        if (onSelectedChange)
            return onSelectedChange(dateWithTime.toISOString());
        return setSelectedDayInternal(dateWithTime.toISOString());
    }, [onSelectedChange, selected]);
    var handleToggleChangeCurrentMonth = React.useCallback(function (calIndex) {
        setShowChangeCurrentYear(-1);
        setShowChangeCurrentMonth(function (prev) { return (prev > -1 ? -1 : calIndex); });
    }, []);
    var handleToggleChangeCurrentYear = React.useCallback(function (calIndex) {
        setShowChangeCurrentMonth(-1);
        setShowChangeCurrentYear(function (prev) { return (prev > -1 ? -1 : calIndex); });
    }, []);
    var handleCurrentDateChange = React.useCallback(function (calIndex, newDateISO) {
        setShowChangeCurrentMonth(-1);
        setShowChangeCurrentYear(-1);
        var newDate = dayjs(newDateISO).add(-calIndex, 'month').toISOString();
        // Need to change the dateInternal relative to which calendar caused the change
        onCurrentDateChange === null || onCurrentDateChange === void 0 ? void 0 : onCurrentDateChange(newDate);
        setDateInternal(newDate);
    }, [onCurrentDateChange]);
    var checkDateHighlighted = React.useCallback(function (date) {
        if (!(highlightedRanges === null || highlightedRanges === void 0 ? void 0 : highlightedRanges.length))
            return false;
        var dateToCheck = dayjs(date).startOf('day');
        return highlightedRanges.some(function (_a) {
            var _b = __read(_a, 2), start = _b[0], end = _b[1];
            var startIsInfinite = start === DATEPICKER_INFINITY;
            var endIsInfinite = end === DATEPICKER_INFINITY;
            var startDate = dayjs(start).startOf('day');
            var endDate = dayjs(end).startOf('day');
            return (startDate.isSame(dateToCheck) ||
                endDate.isSame(dateToCheck) ||
                (startIsInfinite && dateToCheck.isBefore(endDate)) ||
                (endIsInfinite && dateToCheck.isAfter(startDate)) ||
                (dateToCheck.isAfter(startDate) && dateToCheck.isBefore(endDate)));
        });
    }, [highlightedRanges]);
    // renderable memos
    var calendars = React.useMemo(function () {
        var out = [];
        var previousButtonLabel = translate('datepicker.goToPreviousMonthLabel');
        var nextButtonLabel = translate('datepicker.goToNextMonthLabel');
        var prevBtn = (_jsx(Button, { "aria-label": previousButtonLabel, className: classes.previousBtn, disabled: disabled, icon: IconChevronLeft, onClick: handleGoToPreviousMonth, variant: "icon" }));
        var nextBtn = (_jsx(Button, { "aria-label": nextButtonLabel, className: classes.nextBtn, disabled: disabled, icon: IconChevronRight, onClick: handleGoToNextMonth, variant: "icon" }));
        var start = dayjs(dateInternal);
        var _loop_1 = function (i) {
            var month = start.add(i, 'month').toISOString();
            // strings
            var currentDisplayMonth = translate('datepicker.currentDisplayMonth', { date: month });
            var currentDisplayYear = translate('datepicker.currentDisplayYear', { date: month });
            var calendarHeading = translate('datepicker.currentDisplayMonth', { date: month });
            // loop callbacks
            var doToggleChangeCurrentMonth = function () {
                handleToggleChangeCurrentMonth(i);
            };
            var doToggleChangeCurrentYear = function () {
                handleToggleChangeCurrentYear(i);
            };
            var doChangeCurrentDate = function (newDateISO) {
                handleCurrentDateChange(i, newDateISO);
            };
            out.push(_jsxs("div", __assign({ "aria-label": calendarHeading, className: classes.calendarWrapper, "data-datepickercalendarfor": month }, { children: [_jsxs("div", __assign({ className: classes.heading }, { children: [i === 0 && prevBtn, _jsxs("span", { children: [_jsx(Button, __assign({ className: clsx(classes.headingBtn, classes.changeCurrentMonthBtn), disabled: disabled, onClick: doToggleChangeCurrentMonth, tabIndex: (!showChangeCurrentMonth && !showChangeCurrentYear) || showChangeCurrentMonth ? 0 : -1, variant: "tertiary" }, { children: _jsx(Text, __assign({ bold: true, component: "span", level: 2 }, { children: currentDisplayMonth })) })), ' ', _jsx(Button, __assign({ className: clsx(classes.headingBtn, classes.changeCurrentYearBtn), disabled: disabled, onClick: doToggleChangeCurrentYear, tabIndex: (!showChangeCurrentMonth && !showChangeCurrentYear) || showChangeCurrentYear ? 0 : -1, variant: "tertiary" }, { children: _jsx(Text, __assign({ bold: true, component: "span", level: 2 }, { children: currentDisplayYear })) }))] }), calIndexHasNextBtn === i && nextBtn] })), _jsxs("div", __assign({ className: classes.calendarBodyWrapper }, { children: [(showChangeCurrentMonth === i || showChangeCurrentYear === i) && (_jsx(ChangeCurrentMonthOrYear, { classes: classes, currentDate: month, onCurrentDateChange: doChangeCurrentDate, translate: translate, type: showChangeCurrentMonth > -1 ? 'month' : 'year' })), _jsx(CalendarBody, { checkDateHighlighted: checkDateHighlighted, checkDateSelected: checkDateSelected, classes: classes, disabled: disabled, highlightedRanges: highlightedRanges, highlightToday: highlightToday, minSelectableDate: minSelectableDateLocalTime, maxSelectableDate: maxSelectableDateLocalTime, onSelectedChange: handleSelectDayOrDays, range: range, rangeAnchor: rangeAnchor, rangeHover: rangeHover, rangeTuple: rangeTuple, selected: selected !== null && selected !== void 0 ? selected : selectedDayInternal, setRangeAnchor: setRangeAnchor, setRangeHover: setRangeHover, startingDate: month, tabIndex: showChangeCurrentMonth > -1 || showChangeCurrentYear > -1 ? -1 : 0, translate: translate })] }))] }), month));
        };
        for (var i = 0; i < monthsToDisplay; i++) {
            _loop_1(i);
        }
        return out;
    }, [
        calIndexHasNextBtn,
        checkDateHighlighted,
        checkDateSelected,
        classes,
        dateInternal,
        disabled,
        handleCurrentDateChange,
        handleGoToNextMonth,
        handleGoToPreviousMonth,
        handleSelectDayOrDays,
        handleToggleChangeCurrentMonth,
        handleToggleChangeCurrentYear,
        highlightToday,
        highlightedRanges,
        maxSelectableDateLocalTime,
        minSelectableDateLocalTime,
        monthsToDisplay,
        range,
        rangeAnchor,
        rangeHover,
        rangeTuple,
        selected,
        selectedDayInternal,
        showChangeCurrentMonth,
        showChangeCurrentYear,
        translate,
    ]);
    // resize observer
    var resizeObserverRef = React.useRef(new ResizeObserver(function (entries) {
        var e_1, _a;
        try {
            for (var entries_1 = __values(entries), entries_1_1 = entries_1.next(); !entries_1_1.done; entries_1_1 = entries_1.next()) {
                var entry = entries_1_1.value;
                // we don't necessarily care about the actual sizing, we're only doing this to figure out
                // which calendar can have the "nextBtn" in its header.
                // this ensures that if there are multiple calendars and they wrap,
                // the "top row" of calendars are the only ones to have the navigation buttons
                var allMyCalendars = Array.from(entry.target.querySelectorAll('[data-datepickercalendarfor]'));
                if (allMyCalendars.length <= 1)
                    return;
                var _b = __read(allMyCalendars, 1), firstCal = _b[0];
                var firstCalBox = firstCal.getBoundingClientRect();
                for (var i = 1; i < allMyCalendars.length; i++) {
                    var box = allMyCalendars[i].getBoundingClientRect();
                    if (firstCalBox.top !== box.top)
                        return setCalIndexHasNextBtn(i - 1);
                }
                return setCalIndexHasNextBtn(allMyCalendars.length - 1);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (entries_1_1 && !entries_1_1.done && (_a = entries_1.return)) _a.call(entries_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }));
    React.useEffect(function () {
        // We don't want to override the inbound ref or rely on it
        // so we're going to use the traditional way of getting an element by its ID
        var thisPicker = typeof document !== 'undefined' ? document.getElementById(datepickerId) : null;
        if (!thisPicker)
            return;
        // hang onto a scoped variable for the observer so we don't lose it when the component is disposed
        var observer = resizeObserverRef.current;
        observer.observe(thisPicker);
        return function () {
            observer.unobserve(thisPicker);
        };
    }, [datepickerId]);
    // we stringify the rangetuple to prevent calling the
    // handler too much because Array references will always be changing
    var rangeTupleStr = JSON.stringify(rangeTuple);
    React.useEffect(function () {
        var parsedTuple = JSON.parse(rangeTupleStr);
        if (parsedTuple.length)
            onSelectingRange === null || onSelectingRange === void 0 ? void 0 : onSelectingRange(parsedTuple);
    }, [onSelectingRange, rangeTupleStr]);
    React.useEffect(function () {
        // Update the starting date for the calendar(s) rendered
        // so that the selected date, when it changes, is in visible range
        if (!selected)
            return;
        setDateInternal(Array.isArray(selected) ? selected[0] : selected);
    }, [selected]);
    return (_jsxs(RootComponent, __assign({ className: rootClassName, id: datepickerId, ref: ref, role: role, style: style, tabIndex: tabIndex }, rest, { children: [_jsxs("div", __assign({ className: classes.datePickerAndQuickSelectContainer }, { children: [quickSelectEnabled && (_jsx(DatePickerQuickSelect, { classes: classes, disabled: disabled, onSelectedChange: handleSelectDayOrDays, quickSelect: quickSelect, range: range, translate: translate })), calendars, pickerVariant === 'datetimepicker' && (_jsx("div", __assign({ className: classes.timePickerContainer }, { children: _jsx(TimePicker, __assign({ classes: __assign(__assign({}, timePickerProps === null || timePickerProps === void 0 ? void 0 : timePickerProps.classes), { root: clsx(classes.timePicker, (_c = timePickerProps === null || timePickerProps === void 0 ? void 0 : timePickerProps.classes) === null || _c === void 0 ? void 0 : _c.root) }), date: selectedWithTime
                                ? new Date(Array.isArray(selectedWithTime) ? selectedWithTime[0] : selectedWithTime)
                                : null, disabled: disabled, onChange: handleTimePickerChange }, timePickerProps)) })))] })), Boolean(footer) && _jsx("div", __assign({ className: classes.footer }, { children: footer }))] })));
});
DatePicker.displayName = displayName;
